import * as Sentry from '@sentry/react';
import type { Query } from '@tanstack/react-query';
import type { BenchmarkScoring, ListBenchmarkScoringsQuery, ListBenchmarkScoringsQueryVariables } from '@vizit/api';
import { type GetBenchmarkScoringQuery, type GetBenchmarkScoringQueryVariables, pollWhilePending } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';

type BenchmarkScoringQuery = Query<GetBenchmarkScoringQuery, unknown, GetBenchmarkScoringQuery>;
type BenchmarkScoringsQuery = Query<ListBenchmarkScoringsQuery, unknown, ListBenchmarkScoringsQuery>;

const POLLING_TAGS = {
  feature: SENTRY_FEATURE_TAGS.Benchmark,
  stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkScoringPolling,
};

/**
 * How the benchmark page learns that a scoring finished, kept in this one file. The API has no push channel, so each
 * pending scoring's status route is polled on a fixed interval (anchored on when it was requested) and left alone
 * once it is `ready` or `failed`; a scoring stuck pending stops counting after the shared stale window. Landing is
 * what flips the picker row and refreshes the benchmark's lists, so the image lists never poll: with paging, that
 * would re-read every loaded page on every tick. Uploads are tracked on the client while they run, not here; the
 * scorings list does poll briefly once one lands, see `benchmarkScoringsRefetchInterval`.
 */
function benchmarkScoringStatusRefetchInterval(variables: GetBenchmarkScoringQueryVariables) {
  return pollWhilePending<BenchmarkScoringQuery>(
    (query) => (query.state.data?.status === 'pending' ? [query.state.data.requestedAt] : []),
    {
      onTimeout: () => {
        Sentry.captureException(new Error('Benchmark scoring still pending after the stale window.'), {
          level: 'warning',
          tags: POLLING_TAGS,
          extra: { ...variables },
        });
      },
    },
  );
}

/**
 * Whether the list still has to show the API's answer to an upload whose record dates from `landedAt`: the API scores
 * the new image under every category the benchmark is already scored under by requesting those scorings again. There
 * is nothing to wait for while no scoring is settled (a pending one takes the image into its own run), and nothing
 * once any scoring was requested after the record was created.
 */
function awaitsScoringAfterUpload(scorings: BenchmarkScoring[] | undefined, landedAt: string): boolean {
  if (!scorings) {
    return false;
  }
  const landedMs = Date.parse(landedAt);
  return (
    scorings.some((scoring) => scoring.status !== 'pending') &&
    !scorings.some((scoring) => Date.parse(scoring.requestedAt) >= landedMs)
  );
}

/**
 * The scorings list polls in one case only: after an upload lands, until the API has requested the benchmark's
 * scorings again for the new image. The API does that from the storage event, seconds after the file is in S3, so the
 * refresh right after the upload can come too early. The list is then re-read on the shared interval, anchored on the
 * record's creation, and left alone after the stale window; once a scoring shows as requested again, its status poll
 * takes over.
 */
function benchmarkScoringsRefetchInterval(
  variables: ListBenchmarkScoringsQueryVariables,
  uploadLandedAt: string | null,
) {
  return pollWhilePending<BenchmarkScoringsQuery>(
    (query) => (uploadLandedAt && awaitsScoringAfterUpload(query.state.data, uploadLandedAt) ? [uploadLandedAt] : []),
    {
      onTimeout: () => {
        Sentry.captureException(
          new Error('Benchmark scorings not requested again within the stale window after an upload landed.'),
          { level: 'warning', tags: POLLING_TAGS, extra: { ...variables, upload_landed_at: uploadLandedAt } },
        );
      },
    },
  );
}

export { benchmarkScoringStatusRefetchInterval, benchmarkScoringsRefetchInterval };
